// signals-web.ts — the WEB runtime's derived-signal computation for ONE collection.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts (`// TODO: port derived
// logic to JS`). On the web surface every count was 0, every list empty — and the ONE billed
// narration call was handed that empty object, so we were paying a model to describe a catalogue it
// could not see. Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// The headline facts are ported in full and deliberately: they exist so the narration never has to
// compute (or invent) a number. A web build that shipped the aggregates but not the facts would
// hand the model figures with no sentences and invite exactly the fabrication they prevent.

import type { Item, CollectionSet } from "./store";

const round2 = (x: number) => Math.round(x * 100) / 100;

const parseDate = (s: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s ?? "")) return null;
  const t = Date.parse(`${s}T00:00:00Z`);
  return Number.isNaN(t) ? null : new Date(t);
};

/** Mirror of signals.rs::fmt0 — plain integer, thousands separators, no currency symbol. */
const fmt0 = (x: number) => Math.round(x).toLocaleString("en-US", { maximumFractionDigits: 0 });

/** Mirror of signals.rs::item_noun. */
const itemNoun = (kind: string) => (kind.trim() ? kind.trim().toLowerCase() : "items");

/** Mirror of signals.rs::top_counts — count desc, then label asc, capped. */
function topCounts(tally: Map<string, number>, n: number) {
  return [...tally.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, n);
}

/** Mirror of signals.rs::attr_label — a usable display label for an attribute value, else null. */
function attrLabel(v: unknown): string | null {
  if (typeof v === "string") return v.trim() ? v.trim() : null;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "yes" : "no";
  return null;
}

interface SchemaField { key?: unknown; label?: unknown }

export function computeSignalsWeb(
  collectionName: string,
  collectionKind: string,
  attrSchema: unknown,
  items: Item[],
  sets: CollectionSet[],
  todayStr: string,
) {
  const today = parseDate(todayStr) ?? new Date();
  const have = items.filter((i) => !i.wishlist);
  const want = items.filter((i) => i.wishlist);
  const setById = new Map(sets.map((s) => [s.id, s]));
  const setNameOf = (setId: string) => (setId && setById.has(setId) ? setById.get(setId)!.name : null);

  // ── Counts ──────────────────────────────────────────────────────────────────
  const inSets = have.filter((i) => !!i.set_id).length;
  const counts = {
    total: items.length,
    have: have.length,
    want: want.length,
    in_sets: inSets,
    ungrouped: have.length - inSets,
  };

  // ── Value: USER-ENTERED ONLY. Never an appraisal — the note travels with the data. ──
  const values = have.map((i) => i.cost).filter((c): c is number => typeof c === "number");
  const totalLogged = round2(values.reduce((a, b) => a + b, 0));
  const value = {
    items_with_value: values.length,
    total_logged_value: totalLogged,
    avg_logged_value: values.length ? round2(totalLogged / values.length) : null,
    min_logged_value: values.length ? round2(Math.min(...values)) : null,
    max_logged_value: values.length ? round2(Math.max(...values)) : null,
    note: "Every value here is the figure the USER logged for that item — what they paid or assigned. It is NOT a market appraisal. Never present these as what an item is worth.",
  };

  // ── Set completeness ────────────────────────────────────────────────────────
  const setHave = new Map<string, number>();
  const setWant = new Map<string, number>();
  for (const i of have) if (i.set_id) setHave.set(i.set_id, (setHave.get(i.set_id) ?? 0) + 1);
  for (const i of want) if (i.set_id) setWant.set(i.set_id, (setWant.get(i.set_id) ?? 0) + 1);

  const setSignals = sets.map((s) => {
    const haveN = setHave.get(s.id) ?? 0;
    const wantN = setWant.get(s.id) ?? 0;
    const t = typeof s.target_size === "number" && s.target_size > 0 ? s.target_size : null;
    const remaining = t === null ? null : Math.max(0, t - haveN);
    return {
      name: s.name,
      target_size: s.target_size,
      have: haveN,
      want: wantN,
      remaining,
      percent_complete: t === null ? null : Math.min(100, round2((haveN / t) * 100)),
      one_from_complete: remaining === 1,
      complete: remaining === 0,
    };
  });

  // ── Attribute breakdown over the collection's own custom schema ──────────────
  const attribute_breakdown: { key: string; label: string; filled: number; values: { label: string; count: number }[]; distinct_values: number }[] = [];
  if (Array.isArray(attrSchema)) {
    for (const f of attrSchema as SchemaField[]) {
      const key = typeof f?.key === "string" ? f.key : "";
      if (!key) continue;
      const label = typeof f?.label === "string" && f.label ? f.label : key;
      const tally = new Map<string, number>();
      let filled = 0;
      for (const i of have) {
        const v = attrLabel((i.attrs as Record<string, unknown> | undefined)?.[key]);
        if (v === null) continue;
        filled += 1;
        tally.set(v, (tally.get(v) ?? 0) + 1);
      }
      attribute_breakdown.push({ key, label, filled, values: topCounts(tally, 12), distinct_values: tally.size });
    }
  }

  // ── Condition + source (built-in fields) ────────────────────────────────────
  const condTally = new Map<string, number>();
  const srcTally = new Map<string, number>();
  for (const i of have) {
    const c = (i.condition ?? "").trim();
    if (c) condTally.set(c, (condTally.get(c) ?? 0) + 1);
    const s = (i.source ?? "").trim();
    if (s) srcTally.set(s, (srcTally.get(s) ?? 0) + 1);
  }
  const conditions = topCounts(condTally, 10);
  const sources = topCounts(srcTally, 10);

  // ── Acquisition by year + cadence ───────────────────────────────────────────
  const yearTally = new Map<number, number>();
  for (const i of have) {
    const d = parseDate(i.acquired_on);
    if (d) yearTally.set(d.getUTCFullYear(), (yearTally.get(d.getUTCFullYear()) ?? 0) + 1);
  }
  const acquisition_by_year = [...yearTally.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([year, count]) => ({ year: String(year), count }));

  const thisYear = today.getUTCFullYear();
  let acquisition_cadence: {
    this_year: number; last_year: number; delta: number; direction: string;
    busiest_year: string; busiest_year_count: number;
  } | null = null;
  if (yearTally.size) {
    const ty = yearTally.get(thisYear) ?? 0;
    const ly = yearTally.get(thisYear - 1) ?? 0;
    const delta = ty - ly;
    // Rust breaks a tie on count by the LATER year (max_by on (count, year)).
    let busiestYear = thisYear, busiestCount = 0;
    for (const [y, c] of [...yearTally.entries()].sort((a, b) => a[0] - b[0])) {
      if (c >= busiestCount) { busiestCount = c; busiestYear = y; }
    }
    acquisition_cadence = {
      this_year: ty, last_year: ly, delta,
      direction: delta > 0 ? "more" : delta < 0 ? "fewer" : "same",
      busiest_year: String(busiestYear), busiest_year_count: busiestCount,
    };
  }

  // ── Duplicates: same name, case-insensitive, HAVE items only ─────────────────
  const nameTally = new Map<string, { name: string; count: number }>();
  for (const i of have) {
    const key = i.name.trim().toLowerCase();
    if (!key) continue;
    const e = nameTally.get(key) ?? { name: i.name.trim(), count: 0 };
    e.count += 1;
    nameTally.set(key, e);
  }
  const duplicates = [...nameTally.values()]
    .filter((e) => e.count > 1)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 10);

  // ── Value extremes (the user's own figures) ──────────────────────────────────
  const valued = have.filter((i) => typeof i.cost === "number").sort((a, b) => (b.cost as number) - (a.cost as number));
  const toValued = (i: Item) => ({ name: i.name, logged_value: round2(i.cost as number), set_name: setNameOf(i.set_id) });
  const most_valued = valued.slice(0, 5).map(toValued);
  const least_valued = [...valued].reverse().slice(0, 5).map(toValued);

  // ── Recent additions (list order is already newest-first) ────────────────────
  const recent_additions = have.slice(0, 6).map((i) => ({
    name: i.name,
    acquired_on: i.acquired_on ? i.acquired_on : null,
    logged_value: typeof i.cost === "number" ? round2(i.cost) : null,
  }));

  const wishlist = want.slice(0, 20).map((i) => ({ name: i.name, set_name: setNameOf(i.set_id) }));

  const set_summary = buildSetSummary(setSignals);
  const focus = buildFocus(attribute_breakdown);
  const headline_facts = buildHeadlineFacts(
    collectionKind, counts, value, set_summary, focus, acquisition_cadence, duplicates, most_valued, sources, wishlist,
  );

  return {
    schema:
      "oriora.curio.v2-signals: derived aggregates for ONE collection (no free-text notes). Counts, sums, set completeness + a set_summary roll-up, the user's own attribute labels, the collector's dominant attribute (focus), acquisition cadence, duplicates, value extremes (USER-ENTERED values only — never appraisals), recent adds, and the wishlist. headline_facts = pre-ranked, ready-to-say observational sentences already containing the figures — base the narration on THESE, do not invent numbers. HARD CEILING: every value is the figure the USER logged, NEVER a market appraisal — never state what an item is worth. Describe the catalogue, never advise; no buy/sell/trade, no resale, no marketplace. 'one_from_complete' / 'closest_open' describe the user's OWN sets and targets — narrate them as facts, never as 'you should buy X to finish'.",
    generated_for: todayStr,
    collection_name: collectionName,
    collection_kind: collectionKind,
    counts, value,
    sets: setSignals,
    set_summary, attribute_breakdown, focus, conditions, sources,
    acquisition_by_year, acquisition_cadence, duplicates,
    most_valued, least_valued, recent_additions, wishlist, headline_facts,
  };
}

// Declared explicitly rather than derived with ReturnType<typeof computeSignalsWeb>: that form
// makes the function's return type depend on a helper it calls, which TypeScript rejects as
// circular (TS2456).
interface SetSig {
  name: string;
  target_size: number | null;
  have: number;
  want: number;
  remaining: number | null;
  percent_complete: number | null;
  one_from_complete: boolean;
  complete: boolean;
}

/** Mirror of signals.rs::build_set_summary — targeted sets only. */
function buildSetSummary(sets: SetSig[]) {
  const targeted = sets.filter((s) => typeof s.target_size === "number" && (s.target_size as number) > 0);
  const pcts = targeted.map((s) => s.percent_complete).filter((p): p is number => p !== null);
  // The open set with the FEWEST remaining; Rust breaks a tie on the LARGER target.
  const open = targeted.filter((s) => !s.complete && s.remaining !== null);
  let closest: SetSig | null = null;
  for (const s of open) {
    if (
      closest === null ||
      (s.remaining as number) < (closest.remaining as number) ||
      ((s.remaining as number) === (closest.remaining as number) && (s.target_size as number) > (closest.target_size as number))
    ) closest = s;
  }
  return {
    with_target: targeted.length,
    complete: targeted.filter((s) => s.complete).length,
    one_from_complete: targeted.filter((s) => s.one_from_complete).length,
    avg_percent_complete: pcts.length ? round2(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null,
    closest_open: closest
      ? { name: closest.name, have: closest.have, target_size: closest.target_size as number, remaining: closest.remaining as number }
      : null,
  };
}

/** Mirror of signals.rs::build_focus — the collector's single dominant attribute value. */
function buildFocus(breakdown: { label: string; filled: number; values: { label: string; count: number }[] }[]) {
  let best: { field_label: string; value_label: string; count: number; share_pct: number } | null = null;
  for (const ab of breakdown) {
    if (ab.filled < 3) continue;          // too thin to call a "focus"
    const top = ab.values[0];
    if (!top || top.count < 2) continue;  // a leading value of 1 is not a plurality
    const candidate = {
      field_label: ab.label, value_label: top.label, count: top.count,
      share_pct: round2((top.count / ab.filled) * 100),
    };
    if (best === null || (candidate.count > best.count) ||
        (candidate.count === best.count && Math.trunc(candidate.share_pct) > Math.trunc(best.share_pct))) {
      best = candidate;
    }
  }
  return best;
}

/** Mirror of signals.rs::build_headline_facts. Order matters — the narration leads with fact #1. */
function buildHeadlineFacts(
  kind: string,
  counts: { have: number; want: number },
  value: { items_with_value: number; total_logged_value: number },
  ss: ReturnType<typeof buildSetSummary>,
  focus: ReturnType<typeof buildFocus>,
  cadence: { this_year: number; last_year: number; direction: string } | null,
  duplicates: { name: string; count: number }[],
  mostValued: { name: string; logged_value: number }[],
  sources: { label: string; count: number }[],
  wishlist: unknown[],
) {
  const facts: { kind: string; text: string; tone: string }[] = [];
  const noun = itemNoun(kind);

  // 1) Size — the anchor fact.
  if (counts.have > 0) {
    facts.push({
      kind: "size",
      text: counts.want > 0
        ? `You've logged ${fmt0(counts.have)} ${noun}, with ${fmt0(counts.want)} more on your wishlist.`
        : `You've logged ${fmt0(counts.have)} ${noun} in this collection.`,
      tone: "neutral",
    });
  }

  // 2) Completeness — the most satisfying observation a catalogue gives.
  if (ss.complete > 0) {
    facts.push({
      kind: "completeness",
      text: `${fmt0(ss.complete)} of your ${fmt0(ss.with_target)} ${ss.with_target === 1 ? "targeted set is" : "targeted sets are"} ${ss.complete === 1 ? "is" : "are"} fully complete.`
        .replace(" is is", " is").replace(" are are", " are"),
      tone: "good",
    });
  }
  if (ss.closest_open) {
    const co = ss.closest_open;
    const tail = co.remaining === 1 ? "— one short of the target you set." : `— ${fmt0(co.remaining)} short of the target you set.`;
    facts.push({
      kind: "completeness",
      text: `Your “${co.name}” set is ${fmt0(co.have)} of ${fmt0(co.target_size)} ${tail}`,
      tone: co.remaining === 1 ? "good" : "watch",
    });
  }

  // 3) The collector's dominant attribute, in their own labels.
  if (focus) {
    facts.push({
      kind: "focus",
      text: `Your collection leans ${focus.value_label.toLowerCase()}: ${fmt0(focus.count)} of the ${noun} with a ${focus.field_label.toLowerCase()} make up ${Math.round(focus.share_pct)}%.`,
      tone: "neutral",
    });
  }

  // 4) Logged value — the user's own figures, explicitly not an appraisal.
  if (value.items_with_value > 0 && value.total_logged_value > 0) {
    facts.push({
      kind: "value",
      text: `Across the ${fmt0(value.items_with_value)} you valued, your logged total comes to ${fmt0(value.total_logged_value)} (your own figures, not an appraisal).`,
      tone: "neutral",
    });
  }
  if (mostValued[0]) {
    facts.push({
      kind: "value",
      text: `Your highest-valued entry is “${mostValued[0].name}” at ${fmt0(mostValued[0].logged_value)} — the value you logged for it.`,
      tone: "neutral",
    });
  }

  // 5) Cadence this year vs last.
  if (cadence && (cadence.this_year > 0 || cadence.last_year > 0)) {
    if (cadence.direction === "more") {
      facts.push({ kind: "cadence", text: `You've added ${fmt0(cadence.this_year)} ${noun} this year — up from ${fmt0(cadence.last_year)} last year.`, tone: "good" });
    } else if (cadence.direction === "fewer") {
      facts.push({ kind: "cadence", text: `You've added ${fmt0(cadence.this_year)} ${noun} this year, down from ${fmt0(cadence.last_year)} last year.`, tone: "watch" });
    } else {
      facts.push({ kind: "cadence", text: `You've added ${fmt0(cadence.this_year)} ${noun} this year — about the same as last year.`, tone: "neutral" });
    }
  }

  // 6) Source concentration, only when it means something.
  if (sources[0] && sources[0].count >= 3) {
    facts.push({
      kind: "source",
      text: `Most came from one place: ${fmt0(sources[0].count)} ${noun} are logged from “${sources[0].label}”.`,
      tone: "neutral",
    });
  }

  // 7) Wishlist depth — what is still being chased.
  if (wishlist.length >= 3) {
    facts.push({
      kind: "wishlist",
      text: `There ${wishlist.length === 1 ? "is" : "are"} ${fmt0(wishlist.length)} on your wishlist waiting to be found.`,
      tone: "watch",
    });
  }

  // 8) Duplicate names — a plain fact, no judgement.
  if (duplicates[0]) {
    facts.push({
      kind: "duplicate",
      text: `You have ${fmt0(duplicates[0].count)} entries named “${duplicates[0].name}” — possibly duplicates worth a look.`,
      tone: "watch",
    });
  }

  return facts;
}
